import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Heart, 
  GraduationCap, 
  ShoppingCart, 
  CreditCard, 
  Plane, 
  Factory, 
  Car, 
  Utensils, 
  Home,
  ArrowRight,
  CheckCircle
} from 'lucide-react';

const Industries: React.FC = () => {
  const industries = [
    {
      id: 'healthcare',
      title: 'Healthcare & Pharmaceuticals',
      icon: <Heart className="h-12 w-12" />,
      description: 'Digital solutions for healthcare providers, pharmaceutical companies, and medical institutions.',
      color: 'from-red-500 to-pink-600',
      features: [
        'HIPAA Compliant Solutions',
        'Electronic Health Records (EHR)',
        'Telemedicine Platforms',
        'Medical Device Integration',
        'Patient Management Systems',
        'Clinical Decision Support'
      ],
      technologies: ['HL7 FHIR', 'DICOM', 'HIPAA Compliance', 'Medical APIs'],
      caseStudy: 'Healthcare AI Platform'
    },
    {
      id: 'fintech',
      title: 'Banking & Fintech',
      icon: <CreditCard className="h-12 w-12" />,
      description: 'Secure, scalable financial technology solutions for banks, fintech startups, and financial institutions.',
      color: 'from-green-500 to-emerald-600',
      features: [
        'Digital Banking Platforms',
        'Payment Processing Systems',
        'Cryptocurrency Solutions',
        'Risk Management Tools',
        'Regulatory Compliance',
        'Mobile Banking Apps'
      ],
      technologies: ['Blockchain', 'PCI DSS', 'Open Banking APIs', 'Fraud Detection'],
      caseStudy: 'Fintech AI Financial Modeling'
    },
    {
      id: 'ecommerce',
      title: 'E-commerce & Retail',
      icon: <ShoppingCart className="h-12 w-12" />,
      description: 'Complete e-commerce solutions from online stores to complex marketplace platforms.',
      color: 'from-purple-500 to-indigo-600',
      features: [
        'Multi-vendor Marketplaces',
        'Inventory Management',
        'Payment Gateway Integration',
        'Order Fulfillment Systems',
        'Customer Analytics',
        'Mobile Commerce'
      ],
      technologies: ['Shopify', 'Magento', 'WooCommerce', 'Stripe', 'PayPal'],
      caseStudy: 'Fashion Resale Platform'
    },
    {
      id: 'education',
      title: 'Education & EdTech',
      icon: <GraduationCap className="h-12 w-12" />,
      description: 'Innovative educational technology solutions for schools, universities, and online learning platforms.',
      color: 'from-blue-500 to-cyan-600',
      features: [
        'Learning Management Systems',
        'Online Course Platforms',
        'Student Information Systems',
        'Virtual Classrooms',
        'Assessment Tools',
        'Educational Analytics'
      ],
      technologies: ['SCORM', 'LTI Integration', 'Video Streaming', 'AI Tutoring'],
      caseStudy: 'Virtual Learning Platform'
    },
    {
      id: 'travel',
      title: 'Travel & Hospitality',
      icon: <Plane className="h-12 w-12" />,
      description: 'Comprehensive travel and hospitality solutions for hotels, airlines, and tourism companies.',
      color: 'from-orange-500 to-red-600',
      features: [
        'Booking Management Systems',
        'Hotel Management Software',
        'Travel Agency Platforms',
        'Customer Loyalty Programs',
        'Revenue Management',
        'OTA Integration'
      ],
      technologies: ['GDS Integration', 'OTA APIs', 'Revenue Management', 'CRM Systems'],
      caseStudy: 'Hospitality AI Platform'
    },
    {
      id: 'manufacturing',
      title: 'Manufacturing & Industry 4.0',
      icon: <Factory className="h-12 w-12" />,
      description: 'Smart manufacturing solutions and Industry 4.0 technologies for modern production facilities.',
      color: 'from-blue-500 to-cyan-600',
      features: [
        'IoT Integration',
        'Predictive Maintenance',
        'Quality Control Systems',
        'Supply Chain Management',
        'Production Analytics',
        'Digital Twins'
      ],
      technologies: ['IoT Sensors', 'Machine Learning', 'SCADA Systems', 'ERP Integration'],
      caseStudy: 'Smart Factory Solution'
    },
    {
      id: 'automotive',
      title: 'Automotive & Mobility',
      icon: <Car className="h-12 w-12" />,
      description: 'Connected car solutions, fleet management, and mobility services for the automotive industry.',
      color: 'from-indigo-500 to-purple-600',
      features: [
        'Connected Car Platforms',
        'Fleet Management Systems',
        'Ride-sharing Solutions',
        'Vehicle Telematics',
        'Autonomous Vehicle Support',
        'Mobility-as-a-Service'
      ],
      technologies: ['CAN Bus', 'Telematics', 'GPS Tracking', 'Mobile Apps'],
      caseStudy: 'Fleet Management Platform'
    },
    {
      id: 'real-estate',
      title: 'Real Estate & PropTech',
      icon: <Home className="h-12 w-12" />,
      description: 'Property technology solutions for real estate agencies, property managers, and construction companies.',
      color: 'from-teal-500 to-green-600',
      features: [
        'Property Management Systems',
        'Real Estate Portals',
        'Virtual Property Tours',
        'Construction Management',
        'Property Analytics',
        'Tenant Management'
      ],
      technologies: ['3D Visualization', 'AR/VR', 'Property APIs', 'CRM Systems'],
      caseStudy: 'Property Management Platform'
    },
    {
      id: 'food-beverage',
      title: 'Food & Beverage',
      icon: <Utensils className="h-12 w-12" />,
      description: 'Restaurant technology, food delivery platforms, and supply chain solutions for the F&B industry.',
      color: 'from-amber-500 to-orange-600',
      features: [
        'Restaurant Management Systems',
        'Food Delivery Platforms',
        'Inventory Management',
        'POS Integration',
        'Kitchen Display Systems',
        'Supply Chain Optimization'
      ],
      technologies: ['POS Systems', 'Delivery APIs', 'Inventory Tracking', 'Mobile Ordering'],
      caseStudy: 'Restaurant Chain Management'
    }
  ];

  const stats = [
    { number: '50+', label: 'Industries Served' },
    { number: '200+', label: 'Successful Projects' },
    { number: '98%', label: 'Client Satisfaction' },
    { number: '24/7', label: 'Support Available' }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 text-white overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-12">
          <div className="text-center">
            <div className="mb-4">
              <span className="inline-block px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-sm font-medium text-blue-100 border border-white/30">
                🏭 Industry Expertise
              </span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 leading-tight">
              Industries We <span className="text-blue-300 bg-gradient-to-r from-blue-300 to-cyan-300 bg-clip-text text-transparent">Serve</span>
            </h1>
            <p className="text-lg lg:text-xl mb-8 text-blue-100 leading-relaxed max-w-4xl mx-auto">
              We deliver tailored digital solutions across diverse industries, understanding the unique challenges and opportunities each sector presents.
            </p>
            
            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto mt-12">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
                <div className="text-2xl font-bold">9+</div>
                <div className="text-blue-200 text-sm">Industries</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
                <div className="text-2xl font-bold">200+</div>
                <div className="text-blue-200 text-sm">Projects</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
                <div className="text-2xl font-bold">98%</div>
                <div className="text-blue-200 text-sm">Satisfaction</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
                <div className="text-2xl font-bold">24/7</div>
                <div className="text-blue-200 text-sm">Support</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-gradient-to-br from-white via-gray-50/50 to-indigo-50/30 dark:from-gray-900 dark:via-gray-800/50 dark:to-indigo-900/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-indigo-100 dark:bg-indigo-900/50 text-indigo-800 dark:text-indigo-200 rounded-full text-sm font-medium mb-6">
              📊 Industry Impact
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
              Our <span className="text-indigo-600 dark:text-indigo-400">Track Record</span>
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-4xl mx-auto leading-relaxed">
              Proven results across diverse industries, delivering exceptional value and driving digital transformation success.
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="group relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100/50 dark:border-gray-700/50 hover:border-indigo-200 dark:hover:border-indigo-600 hover:-translate-y-3 hover:scale-105">
                  {/* Background Gradient */}
                  <div className="absolute inset-0 bg-gradient-to-br from-indigo-50/50 to-transparent dark:from-indigo-900/20 dark:to-transparent rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  
                  <div className="relative z-10">
                    <div className="text-5xl lg:text-6xl font-bold text-indigo-600 dark:text-indigo-400 mb-4 group-hover:scale-110 transition-transform duration-500">
                      {stat.number}
                    </div>
                    <div className="text-gray-600 dark:text-gray-300 font-bold text-lg group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors duration-300">
                      {stat.label}
                    </div>
                  </div>
                  
                  {/* Hover Effect Border */}
                  <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-indigo-500/20 to-purple-600/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10 blur-xl"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Grid */}
      <section className="py-12 bg-gradient-to-br from-gray-50 via-white to-blue-50/30 dark:from-gray-800 dark:via-gray-900 dark:to-blue-900/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <div className="inline-block px-4 py-2 bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-200 rounded-full text-sm font-medium mb-6">
              🏭 Industry Expertise
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
              Our <span className="text-blue-600 dark:text-blue-400">Industry Focus</span>
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-4xl mx-auto leading-relaxed">
              Deep domain knowledge and proven solutions across key industries, delivering specialized expertise and innovative approaches.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-6xl mx-auto">
            {industries.map((industry) => (
              <div key={industry.id} className="group relative bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm rounded-2xl shadow-md overflow-hidden hover:shadow-lg transition-all duration-300 border border-gray-100/50 dark:border-gray-700/50 hover:border-blue-200 dark:hover:border-blue-600 hover:-translate-y-1 hover:scale-102 mx-4">
                {/* Header with Gradient */}
                <div className={`bg-gradient-to-r ${industry.color} p-6 text-white relative overflow-hidden`}>
                  <div className="absolute inset-0 bg-black/10 group-hover:bg-black/5 transition-colors duration-300"></div>
                  
                  {/* Background Pattern */}
                  <div className="absolute inset-0 opacity-20">
                    <div className="absolute inset-0" style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='20' cy='20' r='2'/%3E%3C/g%3E%3C/svg%3E")`,
                    }}></div>
                  </div>
                  
                  <div className="relative z-10">
                    <div className="flex items-center mb-3">
                      <div className="inline-flex items-center justify-center w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl mr-3 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-lg">
                        {industry.icon}
                      </div>
                      <h3 className="text-lg font-bold">{industry.title}</h3>
                    </div>
                    <p className="text-xs opacity-90 leading-relaxed">{industry.description}</p>
                  </div>
                  
                  {/* Floating Elements */}
                  <div className="absolute top-3 right-3 w-6 h-6 bg-white/20 rounded-full flex items-center justify-center text-white text-xs animate-pulse">
                    ⭐
                  </div>
                  <div className="absolute bottom-3 left-3 w-5 h-5 bg-white/20 rounded-full flex items-center justify-center text-white text-xs animate-bounce">
                    🚀
                  </div>
                </div>
                
                <div className="p-6">
                  <div className="mb-4">
                    <h4 className="text-sm font-bold text-gray-900 dark:text-white mb-3">Key Solutions:</h4>
                    <div className="space-y-2">
                      {industry.features.slice(0, 3).map((feature, featureIndex) => (
                        <div key={featureIndex} className="flex items-center group-hover:text-gray-700 dark:group-hover:text-gray-300 transition-colors duration-300">
                          <CheckCircle className="h-3 w-3 text-green-500 mr-2 flex-shrink-0 group-hover:scale-110 transition-transform duration-300" />
                          <span className="text-gray-600 dark:text-gray-300 text-xs">{feature}</span>
                        </div>
                      ))}
                      {industry.features.length > 3 && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                          +{industry.features.length - 3} more solutions
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <h4 className="text-sm font-bold text-gray-900 dark:text-white mb-3">Technologies:</h4>
                    <div className="flex flex-wrap gap-1">
                      {industry.technologies.slice(0, 3).map((tech, techIndex) => (
                        <span key={techIndex} className="px-2 py-1 bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600 text-gray-700 dark:text-gray-300 text-xs rounded-full font-medium border border-gray-200/50 dark:border-gray-600/50 group-hover:scale-105 transition-transform duration-300">
                          {tech}
                        </span>
                      ))}
                      {industry.technologies.length > 3 && (
                        <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs rounded-full font-medium">
                          +{industry.technologies.length - 3}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">
                      Case Study: {industry.caseStudy}
                    </span>
                    <Link
                      to="/contact"
                      className="group/btn inline-flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-semibold group-hover:scale-105 transition-transform duration-300 text-sm"
                    >
                      Learn More
                      <ArrowRight className="h-3 w-3 ml-1 group-hover/btn:translate-x-1 transition-transform duration-300" />
                    </Link>
                  </div>
                </div>
                
                {/* Hover Effect Border */}
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-blue-500/20 to-indigo-600/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10 blur-xl"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-12 bg-gradient-to-br from-blue-600 via-indigo-700 to-purple-800 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}></div>
        </div>
        
        {/* Floating Elements */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-white/10 rounded-full blur-xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-32 h-32 bg-white/5 rounded-full blur-2xl animate-bounce"></div>
        <div className="absolute top-1/2 left-1/4 w-16 h-16 bg-white/10 rounded-full blur-xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-8">
            <span className="inline-block px-6 py-3 bg-white/20 backdrop-blur-sm rounded-full text-sm font-medium text-blue-100 border border-white/30 mb-6">
              🏭 Industry Transformation
            </span>
          </div>
          
          <h2 className="text-4xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Ready to Transform Your <span className="text-blue-300 bg-gradient-to-r from-blue-300 to-cyan-300 bg-clip-text text-transparent">Industry?</span>
          </h2>
          <p className="text-xl lg:text-2xl text-blue-100 mb-12 max-w-4xl mx-auto leading-relaxed">
            Let's discuss how our industry-specific solutions can drive your business forward and position you as a leader in your sector.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-12">
            <Link
              to="/contact"
              className="group inline-flex items-center justify-center px-10 py-5 text-lg font-semibold rounded-2xl bg-white text-blue-600 hover:bg-blue-50 transition-all duration-300 shadow-2xl hover:shadow-3xl hover:scale-105"
            >
              <span>Start Your Project</span>
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
            <Link
              to="/case-studies"
              className="group inline-flex items-center justify-center px-10 py-5 text-lg font-semibold rounded-2xl border-2 border-white text-white hover:bg-white hover:text-blue-600 transition-all duration-300 hover:scale-105"
            >
              <span>View Case Studies</span>
            </Link>
          </div>
          
          {/* Trust Indicators */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <div className="text-3xl font-bold text-white mb-2">Industry</div>
              <div className="text-blue-200 text-sm">Expertise</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <div className="text-3xl font-bold text-white mb-2">Proven</div>
              <div className="text-blue-200 text-sm">Solutions</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <div className="text-3xl font-bold text-white mb-2">24/7</div>
              <div className="text-blue-200 text-sm">Support</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Industries;
